<template>
  <div class="solar-plant-items">
    <b-row class="pb-2 border-bottom" v-if="shouldShowManagementSystemWarning">
      <b-col lg="12">
        <div class="warning-toast">
          {{ $t('solar_plant_gt_7kwp_warning') }}
        </div>
      </b-col>
    </b-row>

    <!-- Item Packages Section -->
    <b-row class="py-3 border-bottom">
      <b-col lg="12">
        <div class="item-packages-section">
          <div class="d-flex justify-content-between align-items-center mb-2">
            <strong>{{ $t('itemPackages') }}</strong>
          </div>
          <div class="d-flex" v-if="!isBlockReadOnly">
            <multiselect
              :placeholder="$t('select_item_package')"
              v-model="selectedItemPackage"
              :options="activeItemPackagesOptions"
              label="name"
              track-by="number"
              :show-labels="false"
              :allow-empty="true"
              :searchable="true"
              class="flex-grow-1"
            >
              <template slot="singleLabel" slot-scope="{ option }">
                {{ option.name }}
              </template>
              <template slot="option" slot-scope="{ option }">
                {{ option.name }}
              </template>
            </multiselect>
            <b-button
              class="btn btn-sm btn-success ml-2"
              @click="onAddPackage"
              :disabled="!selectedItemPackage"
              style="white-space: nowrap"
            >
              {{ $t('add') }}
            </b-button>
          </div>
          <SolarPlantItemPackages
            :assigned-packages="project.solarPlant.assignedItemPackages || []"
            @remove-package="onRemovePackage"
          />
        </div>
      </b-col>
    </b-row>

    <!-- Inverters Section -->
    <b-row class="py-3 border-bottom">
      <b-col lg="12">
        <div class="inverters-section">
          <div class="d-flex justify-content-between align-items-center mb-2">
            <strong>{{ $t('inverters') }}</strong>
            <button
              v-if="!isBlockReadOnly"
              class="btn btn-sm btn-success"
              @click="openItemBrowser"
              type="button"
              style="white-space: nowrap"
            >
              <i class="fa fa-plus mr-1"></i>
              {{ $t('add_inverter') }}
            </button>
          </div>
          <SolarPlantInverters
            :inverters="project.solarPlant.inverters || []"
            @update-inverters="updateInverters"
          />
        </div>
      </b-col>
    </b-row>

    <!-- Solar Panels Section -->
    <b-row class="py-3 border-bottom">
      <b-col lg="12">
        <div class="solar-panels-section">
          <div class="d-flex justify-content-between align-items-center mb-2">
            <strong>{{ $t('solarPanels') }}</strong>
            <button
              v-if="!isBlockReadOnly"
              class="btn btn-sm btn-success"
              @click="openSolarPanelBrowser"
              type="button"
              style="white-space: nowrap"
            >
              <i class="fa fa-plus mr-1"></i>
              {{ $t('add_solar_panel') }}
            </button>
          </div>
          <SolarPlantSolarPanels
            :solar-panels="project.solarPlant.solarPanels || []"
            @update-solar-panels="updateSolarPanels"
          />
        </div>
      </b-col>
    </b-row>

    <!-- Batteries Section -->
    <b-row class="py-3 border-bottom">
      <b-col lg="12">
        <div class="batteries-section">
          <div class="d-flex justify-content-between align-items-center mb-2">
            <strong>{{ $t('battery') }}</strong>
            <button
              v-if="!isBlockReadOnly"
              class="btn btn-sm btn-success"
              @click="openBatteryBrowser"
              type="button"
              style="white-space: nowrap"
            >
              <i class="fa fa-plus mr-1"></i>
              {{ $t('add_battery') }}
            </button>
          </div>
          <SolarPlantBatteries
            :batteries="project.solarPlant.batteries || []"
            @update-batteries="updateBatteries"
          />
        </div>
      </b-col>
    </b-row>

    <b-row class="py-3 border-bottom">
      <b-col lg="12" md="12" sm="12">
        <div class="wallboxes-section">
          <div class="d-flex justify-content-between align-items-center mb-2">
            <strong>{{ $t('wallbox') }}</strong>
            <button
              v-if="!isBlockReadOnly"
              class="btn btn-sm btn-success"
              @click="openWallboxBrowser"
              type="button"
              style="white-space: nowrap"
            >
              <i class="fa fa-plus mr-1"></i>
              {{ $t('add_wallbox') }}
            </button>
          </div>
          <SolarPlantWallboxes
            :wallboxes="project.solarPlant.wallboxes || []"
            @update-wallboxes="updateWallboxes"
          />
        </div>
      </b-col>
    </b-row>

    <b-row class="py-3">
      <b-col lg="10" md="6" sm="12">
        <strong>{{ $t('additionalComponents') }}</strong>
      </b-col>
      <b-col lg="10" md="6" sm="12">
        <b-form-group
          horizontal
          :label="$t('energyFlowDirectionSensor_short')"
          label-for="energyFlowDirectionSensor"
          label-class="text-md-right"
          :label-cols="4"
          breakpoint="md"
        >
          <multiselect
            :placeholder="$t('energyFlowDirectionSensor_short')"
            v-model="project.solarPlant.energyFlowDirectionSensorItem"
            id="energyFlowDirectionSensor"
            :disabled="isBlockReadOnly"
            :options="filteredEnergyFlowFirectionSensors"
            :allow-empty="true"
            :show-labels="false"
            track-by="_id"
            v-on:search-change="onEnergyFlowDirectionSensorSearch"
            :internal-search="false"
          >
            <template slot="singleLabel" slot-scope="{ option }">
              {{ energyFlowDirectionSensorLabel(option) }}
            </template>
            <template slot="option" slot-scope="{ option }">
              {{ energyFlowDirectionSensorLabel(option) }}
            </template>
          </multiselect>
        </b-form-group>
      </b-col>
      <b-col lg="2" md="6" sm="12"> </b-col>

      <b-col lg="10" md="6" sm="12">
        <b-form-group
          horizontal
          :label="$t('emergencyPowerState')"
          label-for="emergencyPowerState"
          label-class="text-md-right"
          :label-cols="4"
          breakpoint="md"
        >
          <multiselect
            :placeholder="$t('emergencyPowerState')"
            v-model="project.solarPlant.emergencyPowerState"
            :disabled="isBlockReadOnly"
            :options="optionsYesNo"
            :show-labels="false"
            :allow-empty="false"
          >
            <template slot="singleLabel" slot-scope="{ option }"
              ><b-badge :variant="yesNoColor(option)">{{ $t(option) }}</b-badge></template
            >
            <template slot="option" slot-scope="{ option }">
              <b-badge :variant="yesNoColor(option)">
                {{ $t(option) }}
              </b-badge></template
            >
          </multiselect>
        </b-form-group>
      </b-col>
      <b-col lg="2" md="6" sm="12"> </b-col>
    </b-row>

    <!-- Item Browser Modal for Inverters -->
    <Dialog
      :header="$t('add_inverter')"
      :visible.sync="showItemBrowserModal"
      :modal="true"
      :style="{ width: '90vw', maxWidth: '1600px' }"
      :contentStyle="{ height: '70vh', overflow: 'visible' }"
      :closable="true"
    >
      <ItemBrowser
        v-if="showItemBrowserModal"
        context="project"
        :show-pricing="false"
        :show-filters="false"
        :filter-inverters-only="true"
        default-view-mode="cards"
        @add="addInverterToProject"
      />

      <template #footer>
        <button class="btn btn-default" @click="closeItemBrowser">
          {{ $t('close') }}
        </button>
      </template>
    </Dialog>

    <!-- Item Browser Modal for Solar Panels -->
    <Dialog
      :header="$t('add_solar_panel')"
      :visible.sync="showSolarPanelBrowserModal"
      :modal="true"
      :style="{ width: '90vw', maxWidth: '1600px' }"
      :contentStyle="{ height: '70vh', overflow: 'visible' }"
      :closable="true"
    >
      <ItemBrowser
        v-if="showSolarPanelBrowserModal"
        context="project"
        :show-pricing="false"
        :show-filters="false"
        :filter-solar-panels-only="true"
        default-view-mode="cards"
        @add="addSolarPanelToProject"
      />

      <template #footer>
        <button class="btn btn-default" @click="closeSolarPanelBrowser">
          {{ $t('close') }}
        </button>
      </template>
    </Dialog>

    <!-- Item Browser Modal for Batteries -->
    <Dialog
      :header="$t('add') + ' ' + $t('battery')"
      :visible.sync="showBatteryBrowserModal"
      :modal="true"
      :style="{ width: '90vw', maxWidth: '1600px' }"
      :contentStyle="{ height: '70vh', overflow: 'visible' }"
      :closable="true"
    >
      <ItemBrowser
        v-if="showBatteryBrowserModal"
        context="project"
        :show-pricing="false"
        :show-filters="false"
        :filter-batteries-only="true"
        default-view-mode="cards"
        @add="addBatteryToProject"
      />

      <template #footer>
        <button class="btn btn-default" @click="closeBatteryBrowser">
          {{ $t('close') }}
        </button>
      </template>
    </Dialog>

    <!-- Item Browser Modal for Wallboxes -->
    <Dialog
      :header="$t('add') + ' ' + $t('wallbox')"
      :visible.sync="showWallboxBrowserModal"
      :modal="true"
      :style="{ width: '90vw', maxWidth: '1600px' }"
      :contentStyle="{ height: '70vh', overflow: 'visible' }"
      :closable="true"
    >
      <ItemBrowser
        v-if="showWallboxBrowserModal"
        context="project"
        :show-pricing="false"
        :show-filters="false"
        :filter-wallboxes-only="true"
        default-view-mode="cards"
        @add="addWallboxToProject"
      />

      <template #footer>
        <button class="btn btn-default" @click="closeWallboxBrowser">
          {{ $t('close') }}
        </button>
      </template>
    </Dialog>
  </div>
</template>

<script>
import { mapActions, mapGetters } from 'vuex';
import Multiselect from 'vue-multiselect';
import Dialog from 'primevue/dialog';
import ItemBrowser from '@/components/ItemBrowser.vue';
import SolarPlantInverters from '@/components/SolarPlant/SolarPlantInverters.vue';
import SolarPlantSolarPanels from '@/components/SolarPlant/SolarPlantSolarPanels.vue';
import SolarPlantBatteries from '@/components/SolarPlant/SolarPlantBatteries.vue';
import SolarPlantWallboxes from '@/components/SolarPlant/SolarPlantWallboxes.vue';
import SolarPlantItemPackages from '@/components/SolarPlant/SolarPlantItemPackages.vue';
import { yesNo } from '@/helpers/enums';
import { getYesNoStateColor } from '@/helpers/colors';
import { management_system_required } from '@/helpers/solarPlant';
import { AccessBlockMixin } from '@/mixins/accessBlock';

export default {
  mixins: [AccessBlockMixin],
  components: {
    Multiselect,
    Dialog,
    ItemBrowser,
    SolarPlantInverters,
    SolarPlantSolarPanels,
    SolarPlantBatteries,
    SolarPlantWallboxes,
    SolarPlantItemPackages,
  },
  props: {
    project: { type: Object, required: true },
  },
  data() {
    return {
      showItemBrowserModal: false,
      showSolarPanelBrowserModal: false,
      showBatteryBrowserModal: false,
      showWallboxBrowserModal: false,
      filteredEnergyFlowFirectionSensors: [],
      selectedItemPackage: null,
    };
  },
  computed: {
    ...mapGetters(['getItemsBySolarPlant', 'getItemPackages']),

    activeItemPackagesOptions() {
      return this.getItemPackages.filter((pkg) => pkg.active === true);
    },
    optionsYesNo() {
      return yesNo();
    },
    shouldShowManagementSystemWarning() {
      return management_system_required(this.project.solarPlant);
    },
  },
  methods: {
    ...mapActions([
      'fetchItemsBySolarPlant',
      'fetchItemPackagesPaginated',
      'assignItemPackageToProject',
      'removeItemPackageFromProject',
    ]),

    async onAddPackage() {
      if (!this.selectedItemPackage || !this.project?.number) {
        return;
      }

      try {
        await this.assignItemPackageToProject({
          projectNumber: this.project.number,
          itemPackageNumber: this.selectedItemPackage.number,
        });
        // Clear selection after successful assignment
        this.selectedItemPackage = null;
      } catch (error) {
        // Error handling is done in the store action
        console.error('Error assigning package:', error);
      }
    },

    async onRemovePackage(itemPackageNumber) {
      if (!itemPackageNumber || !this.project?.number) {
        return;
      }

      try {
        await this.removeItemPackageFromProject({
          projectNumber: this.project.number,
          itemPackageNumber: itemPackageNumber,
        });
      } catch (error) {
        // Error handling is done in the store action
        console.error('Error removing package:', error);
      }
    },
    yesNoColor(state) {
      return getYesNoStateColor(state);
    },
    /** Method instead of optional chaining in the template, which vue-jest cannot compile. */
    energyFlowDirectionSensorLabel(option) {
      const sensor = option.energyFlowDirectionSensor;
      return `${sensor?.manufacturer ?? ''} - ${sensor?.type ?? ''}`;
    },
    openItemBrowser() {
      this.showItemBrowserModal = true;
    },
    closeItemBrowser() {
      this.showItemBrowserModal = false;
    },
    addInverterToProject(item) {
      console.log('Adding inverter to project:', item);

      // Ensure inverters array exists
      if (!this.project.solarPlant.inverters) {
        this.$set(this.project.solarPlant, 'inverters', []);
      }

      // Check if item already exists in inverters list
      const existingInverterIndex = this.project.solarPlant.inverters.findIndex(
        (inv) => inv.item?._id === item._id || inv.item === item._id
      );

      let updatedInverters;
      const countToAdd = parseInt(item.addRemoveCount || item.quantity || 1, 10);

      if (existingInverterIndex >= 0) {
        // Update count of existing inverter
        updatedInverters = [...this.project.solarPlant.inverters];
        const currentCount = parseInt(updatedInverters[existingInverterIndex].count || 0, 10);
        updatedInverters[existingInverterIndex] = {
          ...updatedInverters[existingInverterIndex],
          count: currentCount + countToAdd,
        };
      } else {
        // Add new inverter to list
        const newInverter = {
          item: item, // Full item object (will be transformed to _id in createPayload)
          count: countToAdd,
        };

        updatedInverters = [...this.project.solarPlant.inverters, newInverter];
      }

      // Update the project
      this.project.solarPlant.inverters = updatedInverters;
    },
    updateInverters(updatedInverters) {
      this.project.solarPlant.inverters = updatedInverters;
    },
    openSolarPanelBrowser() {
      this.showSolarPanelBrowserModal = true;
    },
    closeSolarPanelBrowser() {
      this.showSolarPanelBrowserModal = false;
    },
    openBatteryBrowser() {
      this.showBatteryBrowserModal = true;
    },
    closeBatteryBrowser() {
      this.showBatteryBrowserModal = false;
    },
    openWallboxBrowser() {
      this.showWallboxBrowserModal = true;
    },
    closeWallboxBrowser() {
      this.showWallboxBrowserModal = false;
    },
    addSolarPanelToProject(item) {
      console.log('Adding solar panel to project:', item);

      // Ensure solarPanels array exists
      if (!this.project.solarPlant.solarPanels) {
        this.$set(this.project.solarPlant, 'solarPanels', []);
      }

      // Check if item already exists in solarPanels list
      const existingSolarPanelIndex = this.project.solarPlant.solarPanels.findIndex(
        (panel) => panel.item?._id === item._id || panel.item === item._id
      );

      let updatedSolarPanels;
      const countToAdd = parseInt(item.addRemoveCount || item.quantity || 1, 10);

      if (existingSolarPanelIndex !== -1) {
        // Update existing solar panel count
        const currentCount = parseInt(
          this.project.solarPlant.solarPanels[existingSolarPanelIndex].count || 0,
          10
        );
        updatedSolarPanels = [...this.project.solarPlant.solarPanels];
        updatedSolarPanels[existingSolarPanelIndex] = {
          ...updatedSolarPanels[existingSolarPanelIndex],
          count: currentCount + countToAdd,
        };
      } else {
        // Add new solar panel to list
        const newSolarPanel = {
          item: item, // Full item object (will be transformed to _id in createPayload)
          count: countToAdd,
        };

        updatedSolarPanels = [...this.project.solarPlant.solarPanels, newSolarPanel];
      }

      // Update the project
      this.project.solarPlant.solarPanels = updatedSolarPanels;
    },
    updateSolarPanels(updatedSolarPanels) {
      this.project.solarPlant.solarPanels = updatedSolarPanels;
    },
    addBatteryToProject(item) {
      if (!this.project.solarPlant.batteries) {
        this.$set(this.project.solarPlant, 'batteries', []);
      }

      const existingBatteryIndex = this.project.solarPlant.batteries.findIndex(
        (battery) => battery.item?._id === item._id || battery.item === item._id
      );

      const countToAdd = parseInt(item.addRemoveCount || item.quantity || 1, 10);
      let updatedBatteries;

      if (existingBatteryIndex !== -1) {
        updatedBatteries = [...this.project.solarPlant.batteries];
        const currentCount = parseInt(updatedBatteries[existingBatteryIndex].count || 0, 10);
        updatedBatteries[existingBatteryIndex] = {
          ...updatedBatteries[existingBatteryIndex],
          count: currentCount + countToAdd,
        };
      } else {
        updatedBatteries = [...this.project.solarPlant.batteries, { item, count: countToAdd }];
      }

      this.project.solarPlant.batteries = updatedBatteries;
    },
    updateBatteries(updatedBatteries) {
      this.project.solarPlant.batteries = updatedBatteries;
    },
    addWallboxToProject(item) {
      if (!this.project.solarPlant.wallboxes) {
        this.$set(this.project.solarPlant, 'wallboxes', []);
      }

      const existingWallboxIndex = this.project.solarPlant.wallboxes.findIndex(
        (wallbox) => wallbox.item?._id === item._id || wallbox.item === item._id
      );

      const countToAdd = parseInt(item.addRemoveCount || item.quantity || 1, 10);
      let updatedWallboxes;

      if (existingWallboxIndex !== -1) {
        updatedWallboxes = [...this.project.solarPlant.wallboxes];
        const currentCount = parseInt(updatedWallboxes[existingWallboxIndex].count || 0, 10);
        updatedWallboxes[existingWallboxIndex] = {
          ...updatedWallboxes[existingWallboxIndex],
          count: currentCount + countToAdd,
        };
      } else {
        updatedWallboxes = [...this.project.solarPlant.wallboxes, { item, count: countToAdd }];
      }

      this.project.solarPlant.wallboxes = updatedWallboxes;
    },
    updateWallboxes(updatedWallboxes) {
      this.project.solarPlant.wallboxes = updatedWallboxes;
    },
    onEnergyFlowDirectionSensorSearch(query) {
      this.filteredEnergyFlowFirectionSensors = this.getItemsBySolarPlant(
        'ENERGY_FLOW_SENSOR'
      ).filter((sensor) => {
        if (!query) return true;
        return (
          sensor.manufacturer.toLowerCase().includes(query.toLowerCase()) ||
          sensor.type.toLowerCase().includes(query.toLowerCase())
        );
      });
    },
    async setup() {
      try {
        await this.fetchItemsBySolarPlant('ENERGY_FLOW_SENSOR');
        this.filteredEnergyFlowFirectionSensors =
          this.getItemsBySolarPlant('ENERGY_FLOW_SENSOR') || [];

        // Fetch active item packages
        await this.fetchItemPackagesPaginated({
          pagination: { skip: 0, pageSize: 1000 },
          sorting: { sortField: 'number', sortOrder: 1 },
          filters: { active: { value: 'true' } },
        });
      } catch (error) {
        console.error('Error in setup:', error);
      }
    },
  },
  async mounted() {
    await this.setup();

    // Initialize inverters array if it doesn't exist (for existing projects)
    if (!this.project.solarPlant.inverters) {
      this.$set(this.project.solarPlant, 'inverters', []);
    }

    // Initialize solarPanels array if it doesn't exist (for existing projects)
    if (!this.project.solarPlant.solarPanels) {
      this.$set(this.project.solarPlant, 'solarPanels', []);
    }

    // Initialize batteries array for existing projects
    if (!this.project.solarPlant.batteries) {
      this.$set(this.project.solarPlant, 'batteries', []);
    }

    // Initialize wallboxes array for existing projects
    if (!this.project.solarPlant.wallboxes) {
      this.$set(this.project.solarPlant, 'wallboxes', []);
    }
  },
};
</script>
